import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ReportPeriodFilter from '../components/ReportPeriodFilter';

const HOSTEL_TYPES = ['School Hostel', 'College Hostel'];
const GENDERS = ['Boys', 'Girls'];
const METRICS = ['Ordered', 'Dispatched', 'Received', 'TotalOrdered', 'hostels'];
const METRIC_LABELS = { Ordered: 'Ordered', Dispatched: 'Dispatched', Received: 'Received', TotalOrdered: 'Ordered Hostels', hostels: 'All Hostels' };
const CLICKABLE_METRICS = new Set(['Ordered', 'Dispatched', 'Received']);

const norm = (s) => String(s || '').trim();

const BcDeptReport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drill, setDrill] = useState(null); // { type, subtype, metric }
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState('');
  const [period, setPeriod] = useState('thisMonth');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('authToken');
        const params = new URLSearchParams({ period });
        const res = await fetch(`${API_BASE_URL}/report/orders-bytype?${params.toString()}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const json = await res.json();
        if (res.ok && !json.error) {
          setRows(json.data || []);
        } else {
          setError(json.message || 'Failed to load report');
        }
      } catch (e) {
        setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    })();
  }, [period]);

  const pivot = useMemo(() => {
    const p = {};
    HOSTEL_TYPES.forEach(t => {
      p[t] = {};
      GENDERS.forEach(g => { p[t][g] = { hostels: 0, Ordered: 0, Dispatched: 0, Received: 0 }; });
    });
    rows.forEach(r => {
      const t = norm(r.Type);
      const g = norm(r.subtype);
      if (!p[t] || !p[t][g]) return;
      const ordered = Number(r.Ordered) || 0;
      const dispatched = Number(r.Dispatched) || 0;
      const received = Number(r.Received) || 0;
      p[t][g] = {
        Ordered: ordered,
        Dispatched: dispatched,
        Received: received,
        TotalOrdered: ordered + dispatched + received,
        hostels: Number(r.hostels) || 0,
      };
    });
    return p;
  }, [rows]);

  const openDrill = async (type, subtype, metric) => {
    setDrill({ type, subtype, metric });
    setDrillRows([]);
    setDrillLoading(true);
    setDrillError('');
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({ type, subtype, metric, period });
      const res = await fetch(`${API_BASE_URL}/report/orders-bytype/detail?${params.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (res.ok && !json.error) {
        setDrillRows(json.data || []);
      } else {
        setDrillError(json.message || 'Failed to load detail');
      }
    } catch (e) {
      setDrillError(e.message || 'Network error');
    } finally {
      setDrillLoading(false);
    }
  };

  const cell = (t, g, m) => {
    const v = pivot[t][g][m] || 0;
    const clickable = CLICKABLE_METRICS.has(m);
    return (
      <td
        className={`border border-gray-300 px-3 py-2 text-center text-sm ${clickable ? 'cursor-pointer hover:bg-blue-50' : ''}`}
        onClick={clickable ? () => openDrill(t, g, m) : undefined}
        title={clickable ? 'Click for details' : ''}
      >
        {v}
      </td>
    );
  };

  const totalCol = (t, g) => METRICS.reduce((acc, m) => acc + (pivot[t][g][m] || 0), 0);

  const exportReport1 = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Report 1');
    ws.addRow(['', 'School', '', '', 'College', '', '']);
    ws.addRow(['', 'Boys', 'Girls', 'Total', 'Boys', 'Girls', 'Total']);
    METRICS.forEach(m => {
      ws.addRow([
        METRIC_LABELS[m],
        pivot['School Hostel'].Boys[m], pivot['School Hostel'].Girls[m], (pivot['School Hostel'].Boys[m] + pivot['School Hostel'].Girls[m]),
        pivot['College Hostel'].Boys[m], pivot['College Hostel'].Girls[m], (pivot['College Hostel'].Boys[m] + pivot['College Hostel'].Girls[m]),
      ]);
    });
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), 'BC_Dept_Report_1.xlsx');
  };

  const exportReport2 = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Report 2');
    ws.addRow(['District', 'Hostel ID', 'Hostel Name', 'Item Name', 'Shortage of order', 'Reason']);
    drillRows.forEach(r => {
      const items = (r.items && r.items.length) ? r.items : [{}];
      items.forEach(it => {
        ws.addRow([
          r.district || '',
          r.hostel_id || r.branch_code || '',
          r.hostel_name || r.branch || '',
          it.item_name || '',
          it.shortage || '',
          it.reason || '',
        ]);
      });
    });
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), 'BC_Dept_Report_2.xlsx');
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="text-xl font-semibold text-gray-800">Order Summary</h1>
          <button
            onClick={exportReport1}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
          >
            Export Excel
          </button>
          <ReportPeriodFilter value={period} onChange={setPeriod} />
        </div>

        {loading && <p className="text-sm text-gray-600">Loading…</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-3 py-2 text-sm"></th>
                <th className="border border-gray-300 px-3 py-2 text-sm text-center" colSpan={3}>School</th>
                <th className="border border-gray-300 px-3 py-2 text-sm text-center" colSpan={3}>College</th>
              </tr>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-3 py-2 text-sm"></th>
                <th className="border border-gray-300 px-3 py-2 text-sm">Boys</th>
                <th className="border border-gray-300 px-3 py-2 text-sm">Girls</th>
                <th className="border border-gray-300 px-3 py-2 text-sm">Total</th>
                <th className="border border-gray-300 px-3 py-2 text-sm">Boys</th>
                <th className="border border-gray-300 px-3 py-2 text-sm">Girls</th>
                <th className="border border-gray-300 px-3 py-2 text-sm">Total</th>
              </tr>
            </thead>
            <tbody>
              {METRICS.map(m => (
                <tr key={m} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-3 py-2 text-sm font-medium">{METRIC_LABELS[m]}</td>
                  {cell('School Hostel', 'Boys', m)}
                  {cell('School Hostel', 'Girls', m)}
                  <td className="border border-gray-300 px-3 py-2 text-center text-sm">
                    {(pivot['School Hostel'].Boys[m] || 0) + (pivot['School Hostel'].Girls[m] || 0)}
                  </td>
                  {cell('College Hostel', 'Boys', m)}
                  {cell('College Hostel', 'Girls', m)}
                  <td className="border border-gray-300 px-3 py-2 text-center text-sm">
                    {(pivot['College Hostel'].Boys[m] || 0) + (pivot['College Hostel'].Girls[m] || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {drill && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {METRIC_LABELS[drill.metric]} — {drill.type} {drill.subtype}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={exportReport2}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Export Excel
                </button>
                <button
                  onClick={() => setDrill(null)}
                  className="text-gray-600 hover:text-gray-900 text-2xl leading-none px-2"
                >×</button>
              </div>
            </div>
            <div className="p-4 overflow-auto flex-1">
              {drillLoading && <p className="text-sm text-gray-600">Loading…</p>}
              {drillError && <p className="text-sm text-red-600">{drillError}</p>}
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-sm text-left">District</th>
                    <th className="border border-gray-300 px-3 py-2 text-sm text-left">Hostel ID</th>
                    <th className="border border-gray-300 px-3 py-2 text-sm text-left">Hostel Name</th>
                    <th className="border border-gray-300 px-3 py-2 text-sm text-left">Item Name</th>
                    <th className="border border-gray-300 px-3 py-2 text-sm text-left">Shortage of order</th>
                    <th className="border border-gray-300 px-3 py-2 text-sm text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(!drillLoading && drillRows.length === 0) ? (
                    <tr><td colSpan={6} className="border border-gray-300 px-3 py-4 text-center text-sm text-gray-500">No records</td></tr>
                  ) : (
                    drillRows.flatMap((r, ri) => {
                      const items = (r.items && r.items.length) ? r.items : [{}];
                      return items.map((it, ii) => (
                        <tr key={`${ri}-${ii}`} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2 text-sm">{r.district || ''}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{r.hostel_id || r.branch_code || ''}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{r.hostel_name || r.branch || ''}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{it.item_name || ''}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{it.shortage || ''}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{it.reason || ''}</td>
                        </tr>
                      ));
                    })
                  )}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2">Only shortage items are listed for Partial orders.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BcDeptReport;
